/**
 * ehr#733 / ehr#734 (Jay, 10-31) - Contact Info's required phones.
 *
 * - #734: Mobile phone is a mandatory field.
 * - #733: a phone with any of its contact boxes ticked (OK to call, OK to leave VM, OK to text,
 *   and the AI Calls OK to call / OK to leave VM) needs its number. Mobile is required anyway, so
 *   this adds Home phone and Work phone, each only while one of its boxes is ticked.
 *
 * "Mandatory" works like the drawer's other required fields (First name, Last name, Date of
 * birth, SSN): Save record refuses a blank one, Save draft does not. So a chart that has no mobile
 * yet still opens, and any tab can still be saved as a draft; Save record asks for the number.
 * The API refuses a Demographics save marked `_status: 'record'` the same way (assertRequired-
 * ContactSave), so the rule can't be skipped by calling the API directly. Saves not marked as a
 * record (Save draft, the Add client window's first fill, imports) are not checked here.
 * Nothing here changes stored data or consent answers.
 */
/** Contact Info's phones, mobile first: the number's key, its label, and its boxes. */
export const CONTACT_PHONES = [
    {
        field: 'mobilePhone',
        label: 'Mobile phone',
        always: true,
        boxes: ['mobileOkCall', 'mobileOkVm', 'mobileOkText', 'mobileOkAiCall', 'mobileOkAiVm'],
    },
    {
        field: 'phone',
        label: 'Home phone',
        always: false,
        boxes: ['homeOkCall', 'homeOkVm', 'homeOkAiCall', 'homeOkAiVm'],
    },
    {
        field: 'workPhone',
        label: 'Work phone',
        always: false,
        boxes: ['workOkCall', 'workOkVm', 'workOkAiCall', 'workOkAiVm'],
    },
];
/** The boxes that are ON when left blank (ehr#233, Jay 10-09: calls and VM on the mobile). */
const DEFAULT_ON = new Set(['mobileOkCall', 'mobileOkVm']);
const asObj = (v) => v && typeof v === 'object' && !Array.isArray(v) ? v : {};
const str = (v) => (typeof v === 'string' ? v.trim() : '');
/** Whether a Contact Info box is ticked: stored 'true', or blank with a default-on box. */
export function contactBoxTicked(contact, box) {
    const v = str(asObj(contact)[box]);
    return v === 'true' || (v === '' && DEFAULT_ON.has(box));
}
/** The phone fields Contact Info requires right now (filled or not), mobile first. */
export function requiredContactFields(contact) {
    return CONTACT_PHONES.filter((p) => p.always || p.boxes.some((b) => contactBoxTicked(contact, b))).map((p) => p.field);
}
/** The required phones that are blank in this Contact Info section (empty when none). */
export function requiredContactMissing(contact) {
    const c = asObj(contact);
    const need = new Set(requiredContactFields(c));
    return CONTACT_PHONES.filter((p) => need.has(p.field) && !str(c[p.field])).map((p) => ({
        field: p.field,
        label: p.label,
        message: p.always
            ? `${p.label} is required.`
            : `${p.label} is required because a box under it is ticked. Fill it in, or untick its boxes.`,
    }));
}
/** Whether a Demographics payload is a Save record (the saves the required fields apply to). */
export function isRecordSave(payload) {
    return str(asObj(payload)._status) === 'record';
}
//# sourceMappingURL=required-contact.js.map